import React from 'react';
import { useCombobox, UseComboboxState } from 'downshift';
import styled, { css, useTheme } from 'styled-components/macro';
import { useTranslate } from '@federico.moretti/react-translate';
import { matchSorter } from 'match-sorter';
import { Text } from '../../typography';
import { SPACINGS } from '../../utils/style';
import { IconChevron, IconX } from '../../icons';
import {
  weightMap,
  sizesMap,
  textLineHeight,
  textLetterSpacing,
} from '../../typography/utils';
import { TYPOGRAPHY_WEIGHT } from '../../typography/types';
import { ComboboxSelectProps } from './ComboboxSelect';

const SelectWrapperStyled = styled.div`
  position: relative;

  &:focus {
    outline: none;
  }
`;

type ItemStyledProps = {
  weight: TYPOGRAPHY_WEIGHT;
  highlighted?: boolean;
  colorType?: 'placeholder';
};
const ItemStyled = styled.li<ItemStyledProps>`
  display: flex;
  align-items: center;
  list-style: none;
  color: ${(p) =>
    p.colorType === 'placeholder'
      ? p.theme.colors.secondary80w
      : p.theme.colors.black};
  height: calc(${SPACINGS[10]} - 2px);
  padding: 0 ${SPACINGS[5]};
  background-color: ${(p) =>
    p.highlighted ? p.theme.colors.primary80w : p.theme.colors.white};

  font-family: ${(p) => p.theme.fonts.gtEesti};
  font-weight: ${(p) => weightMap[p.weight]};
  font-size: ${(p) => sizesMap.base.size};
  line-height: ${textLineHeight};
  letter-spacing: ${textLetterSpacing};
`;

const InputWrapper = styled.div`
  position: relative;
  outline: none;
  width: 100%;
  display: flex;
  align-items: center;
`;

type SelectStyledType = {
  isMenuOpen: boolean;
  disabled: boolean;
  isFocused: boolean;
  error: boolean;
};

const SearchInput = styled.input<SelectStyledType>`
  position: relative;
  height: ${SPACINGS[10]};
  width: 100%;
  padding: 0 ${SPACINGS[5]};

  outline: none;
  border: 1px solid
    ${(p) => (p.error ? p.theme.colors.danger : p.theme.colors.secondary80w)};
  border-radius: ${(p) => (p.isMenuOpen ? '10px 10px 0 0' : '10px')};
  background-color: ${(p) =>
    p.disabled ? p.theme.colors.secondary90w : 'transparent'};
  cursor: ${(p) => (p.disabled ? 'not-allowed' : 'auto')};

  ${(p) =>
    p.isMenuOpen &&
    css`
      box-shadow: 0 0 0 2px ${p.theme.colors.primary};
      border: 1px solid
        ${p.error ? p.theme.colors.danger : p.theme.colors.secondary80w};
    `}

  &:focus {
    box-shadow: 0 0 0 2px ${(p) => p.theme.colors.primary};
    border: 1px solid
      ${(p) => (p.error ? p.theme.colors.danger : p.theme.colors.black)};
  }

  &:hover {
    border: 1px solid ${(p) => p.theme.colors.black};
  }

  &::placeholder {
    color: ${(p) => p.theme.colors.secondary80w};
  }

  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

  font-family: ${(p) => p.theme.fonts.gtEesti};
  font-weight: ${(p) => weightMap.normal};
  font-size: ${(p) => sizesMap.base.size};
  line-height: ${textLineHeight};
  letter-spacing: ${textLetterSpacing};
`;

export const CloseButton = styled.button`
  position: absolute;
  right: 10px;
  background-color: transparent;
  border: none;
  cursor: pointer;
`;

export const ClearButton = styled.button`
  position: absolute;
  right: 30px;
  background-color: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
`;

const SelectContainer = styled.div`
  width: ${(p) => p.theme.dimensions.inputWidth};
`;

const MenuWrapper = styled.ul`
  margin: 0;
  padding: 0;
  border-top: 0;
  outline: none;
  position: absolute;
  right: 0;
  left: 0;
  z-index: ${(p) => p.theme.zIndexes.overlay};
`;

const MenuStyled = styled.div`
  outline: none;
  padding: 0;
  margin: 0;
  margin-top: 2px;
  width: 100%;
  max-height: 10em;
  overflow: auto;
  background-color: ${(p) => p.theme.colors.white};
  border: 1px solid ${(p) => p.theme.colors.black};
  border-radius: 0 0
    ${(p) =>
      `${p.theme.dimensions.borderRadius} ${p.theme.dimensions.borderRadius}`};
  z-index: ${(p) => p.theme.zIndexes.overlay};
  box-shadow: 0 0 0 2px ${(p) => p.theme.colors.primary};

  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
`;

function ComboboxSelect<Item>(props: ComboboxSelectProps<Item>) {
  const {
    id,
    label,
    items,
    initialSelectedItem,
    rowComponent,
    searchKeys = [],
    itemToString,
    onItemChange,
    disabled,
    style,
    clearItemAfterChange,
    placeholder = 'Select...',
    error,
    name,
    testid,
  } = props;

  const theme = useTheme();
  const { t } = useTranslate();
  const newId = id ?? name;

  // to handle controlled and uncontrolled selectedItem
  const selectProps: Partial<UseComboboxState<Item>> = {};
  if (typeof props.selectedItem !== 'undefined') {
    selectProps.selectedItem = props.selectedItem;
  }

  const [inputItems, setInputItems] = React.useState(items);

  const {
    isOpen,
    selectedItem,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    highlightedIndex,
    getItemProps,
    getComboboxProps,
    getInputProps,
    setInputValue,
    inputValue,
  } = useCombobox({
    id: newId,
    items: inputItems,
    itemToString,
    initialSelectedItem,
    stateReducer: (_, actionAndChanges) => {
      const { changes, type } = actionAndChanges;
      if (changes.inputValue === '') {
        setTimeout(() => onItemChange(null));
        changes.selectedItem = null;
        setInputItems(items);
      }
      if (changes.inputValue && changes.inputValue?.length > 0) {
        setInputItems(
          matchSorter(items, changes.inputValue, {
            keys: [itemToString, ...searchKeys],
          })
        );
      }
      if (
        changes.selectedItem &&
        (type === useCombobox.stateChangeTypes.ItemClick ||
          type === useCombobox.stateChangeTypes.InputKeyDownEnter)
      ) {
        setTimeout(() => onItemChange(changes.selectedItem));

        if (clearItemAfterChange) {
          return {
            inputValue: '',
          } as UseComboboxState<Item>;
        }
      }
      return changes;
    },
    ...selectProps,
  });

  function clearSelection() {
    onItemChange(null);
    setInputValue('');
  }

  return (
    <SelectContainer style={style}>
      {label && (
        <>
          {typeof label === 'string' ? (
            <Text type="label" {...getLabelProps()}>
              {label}
            </Text>
          ) : (
            label(getLabelProps())
          )}
        </>
      )}
      <SelectWrapperStyled>
        <InputWrapper {...getComboboxProps()}>
          <SearchInput
            id={newId}
            data-testid={`${testid}-input`}
            placeholder={placeholder}
            error={error}
            isMenuOpen={isOpen}
            {...getInputProps({ disabled })}
          />
          {inputValue.length > 0 && !disabled && (
            <ClearButton
              type="button"
              onClick={clearSelection}
              data-testid={`${testid}-clear-button`}
            >
              <IconX scale={0.75} stroke={theme.colors.secondary60w} />
            </ClearButton>
          )}
          <CloseButton
            type="button"
            {...getToggleButtonProps({ disabled })}
            data-testid={`${testid}-menu-button`}
          >
            <IconChevron
              stroke={theme.colors.primary20b}
              direction={isOpen ? 'top' : 'bottom'}
            />
          </CloseButton>
        </InputWrapper>
        <MenuWrapper {...getMenuProps()} data-testid={`${testid}-menu`}>
          {isOpen ? (
            <MenuStyled>
              {inputItems.length > 0 ? (
                inputItems.map((item, index) => (
                  <ItemStyled
                    data-testid={`${testid}-item-${index}`}
                    highlighted={highlightedIndex === index}
                    key={index}
                    {...getItemProps({
                      item,
                      index,
                    })}
                  >
                    {rowComponent ? (
                      rowComponent(item)
                    ) : (
                      <Text
                        weight={
                          itemToString(item) === itemToString(selectedItem)
                            ? 'bold'
                            : 'normal'
                        }
                        margin="none"
                      >
                        {itemToString(item)}
                      </Text>
                    )}
                  </ItemStyled>
                ))
              ) : (
                <ItemStyled weight="light" colorType="placeholder">
                  {t('noOptions')}
                </ItemStyled>
              )}
            </MenuStyled>
          ) : null}
        </MenuWrapper>
      </SelectWrapperStyled>
      {typeof error === 'string' && (
        <Text
          id={`${newId}-error`}
          style={{ color: theme.colors.danger, marginTop: SPACINGS[2] }}
          margin="none"
        >
          {error}
        </Text>
      )}
    </SelectContainer>
  );
}

export default React.memo(ComboboxSelect) as typeof ComboboxSelect;
